import { useState, useMemo } from 'react';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';

// Types pour la configuration des onglets
export interface TabConfig {
  id: number | string;
  label: string; // Clé de traduction
  content?: React.ReactNode; // Contenu personnalisé
  disabled?: boolean;
  hidden?: boolean;
}

export interface UseConfigTabsOptions {
  defaultTab?: number | string;
  translationNamespaces?: NsKey[]; // Namespaces pour les traductions
}

export interface UseConfigTabsReturn {
  activeTab: number | string;
  setActiveTab: (tab: number | string) => void;
  tabs: TabConfig[];
  visibleTabs: TabConfig[];
  getTabLabel: (tab: TabConfig) => string;
  isTabActive: (tabId: number | string) => boolean;
  isTabDisabled: (tabId: number | string) => boolean;
  getActiveTabContent: () => React.ReactNode | undefined;
}

/**
 * Hook générique pour gérer des onglets configurables
 * @param tabsConfig Configuration des onglets
 * @param options Options du hook
 * @returns Objet avec les propriétés et méthodes pour gérer les onglets
 */
export const useConfigTabs = (
  tabsConfig: TabConfig[],
  options: UseConfigTabsOptions = {}
): UseConfigTabsReturn => {
  const {
    defaultTab = tabsConfig[0]?.id,
    translationNamespaces = [NsKey.Common],
  } = options;

  const [activeTab, setActiveTab] = useState<number | string>(defaultTab);

  // Récupération des traductions
  const { t } = useDynamicTranslation(translationNamespaces);

  // Filtrage des onglets visibles
  const visibleTabs = useMemo(() =>
    tabsConfig.filter(tab => !tab.hidden),
    [tabsConfig]
  );

  // Fonction pour récupérer le label traduit d'un onglet
  const getTabLabel = (tab: TabConfig): string => {
    return t(tab.label);
  };

  // Vérifier si un onglet est actif
  const isTabActive = (tabId: number | string): boolean => {
    return activeTab === tabId;
  };

  // Vérifier si un onglet est désactivé
  const isTabDisabled = (tabId: number | string): boolean => {
    const tab = tabsConfig.find(t => t.id === tabId);
    return tab?.disabled || false;
  };

  // Récupérer le contenu de l'onglet actif
  const getActiveTabContent = (): React.ReactNode | undefined => {
    const activeTabConfig = tabsConfig.find(tab => tab.id === activeTab);
    return activeTabConfig?.content;
  };

  return {
    activeTab,
    setActiveTab,
    tabs: tabsConfig,
    visibleTabs,
    getTabLabel,
    isTabActive,
    isTabDisabled,
    getActiveTabContent
  };
};
